import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function AudiobooksPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Schema.org JSON-LD
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Audiobooks – Adrian M. Hurtado',
      url: `${siteUrl}/audiobooks`,
      description: 'Audiobook editions of children\'s books by Adrian M. Hurtado – coming soon. Professional narration of The Dawn, Acro Bat, Toad\'s Tools, Mary Had A Little Lamb, and more.',
      author: { '@type': 'Person', name: 'Adrian M. Hurtado' },
      isPartOf: { '@type': 'WebSite', name: 'Adrian M. Hurtado', url: siteUrl },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Audiobooks', item: `${siteUrl}/audiobooks` },
      ],
    };

    const scripts: HTMLScriptElement[] = [];

    [webPageSchema, breadcrumbSchema].forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      script.setAttribute('data-schema', 'audiobooks');
      document.head.appendChild(script);
      scripts.push(script);
    });

    const prevTitle = document.title;
    document.title = 'Audiobooks | Adrian M. Hurtado – Children\'s Books Author';

    // Update meta description for this page
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const prevDesc = metaDesc?.content ?? '';
    if (metaDesc) {
      metaDesc.content = 'Audiobook editions of children\'s books by Adrian M. Hurtado – coming soon. Professional narration of The Dawn, Acro Bat, Toad\'s Tools & more.';
    }

    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
      document.title = prevTitle;
      if (metaDesc) metaDesc.content = prevDesc;
    };
  }, []);

  const audiobooks = [
    {
      title: 'The Dawn',
      image: 'https://m.media-amazon.com/images/I/81Cw42zfDML._SY522_.jpg',
      status: 'Coming Soon',
      description: 'Experience the captivating story of The Dawn in audio format, narrated with emotion and depth.',
      duration: 'TBA'
    },
    {
      title: 'Inside Me',
      image: 'https://m.media-amazon.com/images/I/81bxLzKr-KL._SY522_.jpg',
      status: 'Coming Soon',
      description: 'Listen to this touching narrative brought to life through professional narration.',
      duration: 'TBA'
    },
    {
      title: 'Mary Had A Little Lamb',
      image: 'https://m.media-amazon.com/images/I/810E-rIOBGL._SY522_.jpg',
      status: 'Coming Soon',
      description: 'A delightful children\'s story perfect for bedtime listening and family enjoyment.',
      duration: 'TBA'
    },
    {
      title: 'The Tennis Jock',
      image: 'https://m.media-amazon.com/images/I/71IPF8843NL._SY522_.jpg',
      status: 'Coming Soon',
      description: 'An engaging tale that comes alive through expert audio narration.',
      duration: 'TBA'
    },
    {
      title: 'Acro Bat',
      image: 'https://m.media-amazon.com/images/I/91AtHwzLmGL._SY522_.jpg',
      status: 'Coming Soon',
      description: 'Join the adventure with this exciting audiobook experience for young listeners.',
      duration: 'TBA'
    },
    {
      title: "Toad's Tools",
      image: 'https://m.media-amazon.com/images/I/81h0TU89dPL._SY522_.jpg',
      status: 'Coming Soon',
      description: 'A charming story that will entertain children and parents alike in audio format.',
      duration: 'TBA'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50 border-b border-teal-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/e9b4463a34ddb160942b6a3ff8748621.jpeg" 
                alt="Adrian Hurtado Logo" 
                className="w-12 h-12 rounded-full object-cover ring-2 ring-teal-400"
              />
              <span className="text-xl font-semibold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">Adrian M. Hurtado</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
              <a href="/#about" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">About the Author</a>
              <a href="/#books" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Books</a>
              <Link to="/audiobooks" className="text-sm font-medium text-teal-600 hover:text-emerald-600 transition-colors whitespace-nowrap cursor-pointer">Audiobooks</Link>
              <a href="/#contact" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Contact Us</a>
            </div>
            <button className="md:hidden text-teal-600">
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-100 to-emerald-100 rounded-full mb-6">
            <i className="ri-headphone-line text-teal-600"></i>
            <span className="text-sm font-medium text-teal-700">Audio Experience</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-700 bg-clip-text text-transparent mb-6 leading-tight">
            Audiobooks
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Discover Adrian M. Hurtado's stories in a whole new way. Our audiobook collection brings these beloved tales to life with professional narration, perfect for listening at home, in the car, or on the go.
          </p>
        </div>
      </section>

      {/* Audiobooks Grid */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {audiobooks.map((audiobook, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-lg shadow-teal-100 border border-teal-100 hover:shadow-2xl hover:shadow-teal-200 transition-all duration-300">
                <div className="w-full h-80 overflow-hidden bg-gradient-to-br from-teal-50 to-emerald-50 relative flex items-center justify-center">
                  <img 
                    src={audiobook.image} 
                    alt={audiobook.title}
                    className="h-full w-auto object-contain"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 text-white">
                      <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                        <i className="ri-headphone-line text-xl"></i>
                      </div>
                      <span className="text-sm font-medium">Audio Edition</span>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{audiobook.title}</h3>
                    <span className="px-3 py-1 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {audiobook.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    {audiobook.description}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-gray-500">
                      <i className="ri-time-line"></i>
                      <span>{audiobook.duration}</span>
                    </div>
                    <button className="text-teal-600 hover:text-emerald-600 font-medium transition-colors cursor-pointer whitespace-nowrap">
                      Notify Me
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-white to-teal-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white rounded-2xl p-12 shadow-xl shadow-teal-100 border border-teal-100">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-notification-line text-3xl text-teal-600"></i>
            </div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent mb-4">
              Get Notified When Audiobooks Launch
            </h2>
            <p className="text-gray-700 mb-8 leading-relaxed">
              Be the first to know when Adrian M. Hurtado's audiobooks become available. Sign up for notifications and never miss a release.
            </p>
            <a 
              href="/#contact" 
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-teal-700 hover:to-emerald-700 transition-all shadow-lg shadow-teal-200 whitespace-nowrap cursor-pointer"
            >
              Contact Us for Updates
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent mb-12 text-center">
            Why Choose Audiobooks?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-car-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Listen Anywhere</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Enjoy stories during your commute, workout, or while doing household chores.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-parent-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Family Bonding</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Perfect for family listening time, creating shared experiences and memories.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-read-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Enhanced Experience</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Professional narration brings characters and stories to life in new ways.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/e9b4463a34ddb160942b6a3ff8748621.jpeg" 
                  alt="Adrian Hurtado Logo" 
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-white/50"
                />
                <span className="text-lg font-semibold">Adrian M. Hurtado</span>
              </Link>
              <p className="text-sm text-teal-50">
                Author, Educator, Storyteller
              </p>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="text-sm text-teal-50 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><a href="/#about" className="text-sm text-teal-50 hover:text-white transition-colors cursor-pointer">About</a></li>
                <li><a href="/#books" className="text-sm text-teal-50 hover:text-white transition-colors cursor-pointer">Books</a></li>
                <li><Link to="/audiobooks" className="text-sm text-teal-50 hover:text-white transition-colors cursor-pointer">Audiobooks</Link></li>
                <li><a href="/#contact" className="text-sm text-teal-50 hover:text-white transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold mb-4">Connect</h3>
              <div className="flex gap-4">
                <a href="https://www.amazon.com/stores/Adrian-M.-Hurtado/author/B00JER2ZOE" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer">
                  <i className="ri-amazon-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="text-sm text-teal-50">
              © 2025 Adrian M. Hurtado. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
